<script setup lang="ts">
import { computed, type ComputedRef, toValue } from 'vue'
import { useI18n } from 'vue-i18n'

const { n } = useI18n()

const { min, max, value, exceeded } = defineProps({
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  value: {
    type: Number,
    required: true,
    validator(value: number, { min, max }: { min: number; max: number }) {
      return value >= min && value <= max
    },
  },
  height: {
    type: String,
    required: false,
    default: '16px',
  },
  exceeded: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const progress: ComputedRef<number> = computed(() => {
  if (value === max) {
    return 100
  }

  if (value === min) {
    return 0
  }

  return Number((100 * (value / max)).toPrecision(2))
})

const barWidth = computed(() => {
  return Math.min(progress.value, 100) + '%'
})

const notStartedOrEmpty: ComputedRef<boolean> = computed(() => value === min || min === max)

const progressBarClasses = computed(() => ({
  'progress-bar': true,
  'bg-secondary': toValue(notStartedOrEmpty),
  'bg-danger': min !== max && (value === max || exceeded),
  'bg-info': !exceeded && value > min && value < max,
}))
</script>

<template>
  <div class="progress">
    <div
      role="progressbar"
      :class="progressBarClasses"
      :aria-valuenow="value"
      :aria-valuemin="min"
      :aria-valuemax="max"
    >
      <span class="ml-1 mr-1">
        <slot name="value-text">{{ n(progress / 100, 'percent') }}</slot>
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.progress {
  height: v-bind('height');
  margin-bottom: 0;

  &-bar {
    height: 100%;
    min-width: fit-content;
    width: v-bind('barWidth');

    &.bg-info {
      background-color: #48b0f7 !important;
      color: #fff !important;
    }

    &.bg-secondary {
      color: #333 !important;
    }

    &.bg-info,
    &.bg-danger,
    &.bg-secondary {
      border: 0 !important;
    }
  }
}
</style>
